import type { LinkProps } from '@tanstack/react-router'

import { ADMIN_ROLES, holdsAny, ROOT_ROLES } from '@brickup/shared/constants'
import { UserRole } from '@brickup/shared/enums'
import { DEVELOPMENT } from '@brickup/shared/locales/de/development'
import { PRODUCT_CLASSIFICATION_KIND_LABELS as LISTS } from '@brickup/shared/locales/de/enum-labels'
import { NAV } from '@brickup/shared/locales/de/nav'

export type SystemArea = {
  /** Doubles as the key: `NAV_PERMISSIONS` is derived from these paths. */
  href: LinkProps['to']
  label: string
  /**
   * Who may open this one page, where that is narrower than the group.
   *
   * Narrower only — the group's list is the ceiling. An area listed here
   * disappears from the rail for everyone else, so a page whose contents are
   * not for the whole group is not merely refused, it is not offered.
   */
  roles?: readonly UserRole[]
}

export type SystemGroup = {
  id: string
  label: string
  /** Who may open the group's pages. */
  roles: readonly UserRole[]
  /**
   * Drops the area column and gives the page the whole width.
   *
   * For a group whose page brings its own list and pane — the configurator —
   * where a second navigation beside it would be a column naming one thing.
   * Stated rather than inferred from the number of areas: a group that happens
   * to hold one page is still one an operator walks into by name.
   */
  fullWidth?: boolean
  /**
   * Who may change what is on them. Defaults to whoever may open them; a role
   * in `roles` but not here reads every page of the group and finds every
   * control disabled.
   */
  writeRoles?: readonly UserRole[]
  /** The segment the group's pages share, where they share one. */
  basePath?: string
  areas: readonly SystemArea[]
}

const ADMIN = ADMIN_ROLES
const ROOT = ROOT_ROLES

/**
 * The System area, in two levels: the groups the header offers and the pages
 * each one holds.
 *
 * The split follows who may open what — ADMIN configures the catalogue and the
 * accounts it is built from, ROOT the running system and the app-wide settings
 * — so a role sees whole groups appear and disappear, and the header can offer
 * a group's first page as the destination of its tab. `NAV_PERMISSIONS` is
 * generated from `roles`, so an area cannot reach the router without one.
 *
 * A group says for itself whether it is shown without the area column, which
 * only the configurator is: it carries its own list and pane and needs the
 * width.
 */
export const SYSTEM_GROUPS: readonly SystemGroup[] = [
  {
    id: 'administration',
    label: NAV.administration,
    roles: ADMIN,
    basePath: '/internal/system/administration',
    areas: [
      { href: '/internal/system/administration/users', label: NAV.users },
      // Beside the accounts, because that is what it manages: an assistant
      // reaches the app as the account that connected it, and cutting one is
      // the same act as the account controls beside it. The group is also what
      // lets an admin cut one — `Globale Konfiguration` writes as ROOT only,
      // and its pages sit in a read-only scope for everyone else.
      { href: '/internal/system/administration/mcp', label: NAV.mcp },
      {
        href: '/internal/system/administration/element-types',
        label: NAV.elementTypes,
      },
      // Beside the lists a product is filed under, because it is the other
      // half of the same question: the lists say where a product belongs, the
      // properties say what it is measured in.
      {
        href: '/internal/system/administration/product-properties',
        label: NAV.productProperties,
      },
      // One page per list rather than four stacked on one: they are kept
      // independently, and a rail entry is what makes each reachable by name.
      {
        href: '/internal/system/administration/classifications/categories',
        label: LISTS.CATEGORY,
      },
      {
        href: '/internal/system/administration/classifications/groups',
        label: LISTS.GROUP,
      },
      {
        href: '/internal/system/administration/classifications/design-lines',
        label: LISTS.DESIGN_LINE,
      },
      {
        href: '/internal/system/administration/classifications/standards',
        label: LISTS.STANDARD,
      },
      // What every project's finances are built from: the construction
      // phases and the payment plans that point at them, and the Firmen a
      // payable contract names.
      {
        href: '/internal/system/administration/payment-plans',
        label: NAV.paymentPlans,
      },
      {
        href: '/internal/system/administration/parties',
        label: NAV.contractParties,
      },
      { href: '/internal/system/administration/bank', label: NAV.bank },
    ],
  },
  {
    id: 'configurator',
    label: NAV.configurator,
    roles: ADMIN,
    fullWidth: true,
    areas: [{ href: '/internal/system/configurator', label: NAV.configurator }],
  },
  {
    id: 'operations',
    label: NAV.operations,
    roles: ADMIN,
    writeRoles: ROOT,
    basePath: '/internal/system/operations',
    areas: [
      { href: '/internal/system/operations/email', label: NAV.email },
      { href: '/internal/system/operations/logs', label: NAV.logs },
      // Under the logs it explains: a failed job is read there and steered
      // from here.
      { href: '/internal/system/operations/queue', label: NAV.queue },
      { href: '/internal/system/operations/history', label: NAV.changeHistory },
      // Beside the queue and the history: the three are what an operator
      // reaches for after a change the application did not make.
      {
        href: '/internal/system/operations/suggestions',
        label: NAV.suggestionPass,
      },
      {
        href: '/internal/system/operations/permissions',
        label: NAV.permissions,
      },
      // The nightly job and what each of its tasks did.
      {
        href: '/internal/system/operations/housekeeping',
        label: NAV.housekeeping,
      },
      // ROOT alone: the archive this page offers is the whole database, and
      // downloading it is the act that matters, not starting it.
      {
        href: '/internal/system/operations/backup',
        label: NAV.backup,
        roles: ROOT,
      },
    ],
  },
  {
    id: 'settings',
    label: NAV.globalConfig,
    roles: ADMIN,
    writeRoles: ROOT,
    basePath: '/internal/system/settings',
    areas: [
      { href: '/internal/system/settings/hubspot', label: NAV.hubspot },
      { href: '/internal/system/settings/email', label: NAV.emailConfig },
      { href: '/internal/system/settings/advisor', label: NAV.defaultAdvisor },
      { href: '/internal/system/settings/cleanup', label: NAV.listingCleanup },
      { href: '/internal/system/settings/images', label: NAV.imagePipeline },
      { href: '/internal/system/settings/ai', label: NAV.ai },
    ],
  },
  /**
   * What a developer does to an environment that is not prod.
   *
   * ROOT throughout, reads included: the one page here hands over the whole
   * configured state of the environment or replaces it, so there is nothing on
   * it to look at without acting. It is also where the next thing of that kind
   * goes, rather than beside the pages an admin keeps.
   */
  {
    id: 'development',
    label: DEVELOPMENT.title,
    roles: ROOT,
    basePath: '/internal/system/development',
    areas: [{ href: '/internal/system/development/sync', label: NAV.sync }],
  },
]

/**
 * Where a group opens: its first page.
 *
 * A group has no page of its own, so the first area stands in for it — the
 * header's tab leads there, and so does the redirect on the group's own
 * segment. Reordering the list above moves the entry point with it.
 */
export const firstAreaOf = (group: SystemGroup) => group.areas[0].href

/** Whether a role may open a group at all. The one predicate the header's
 *  list, the redirect and the guard all read. */
export const canOpenSystemGroup = (
  group: SystemGroup,
  roles: readonly UserRole[] | null,
) => holdsAny(roles, group.roles)

/**
 * The same answer for a segment nothing renders: the named group, or — for
 * System itself, which has no group of its own — the first one the role may
 * open.
 *
 * Named by `id` rather than by the path it sits at, so the group's route and
 * the table's `basePath` are written once each.
 */
export function firstAreaUnder(
  groupId: string | undefined,
  roles: readonly UserRole[] | null,
): LinkProps['to'] {
  const group =
    (groupId && SYSTEM_GROUPS.find(one => one.id === groupId)) ||
    SYSTEM_GROUPS.find(one => canOpenSystemGroup(one, roles)) ||
    SYSTEM_GROUPS[0]

  return firstAreaOf(group)
}
